import { LinkListEditor } from "../../components/LinkListEditor";
import { normalizeLinks, linksAreValid } from "../../utils/linkLists";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { templateTypeBadgeColors } from "../../theme/theme";
import {
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  ChevronLeft,
  Layers,
  ChevronDown,
  CheckCircle2,
  Target,
  Megaphone,
  Globe,
  Share2,
  PenTool,
  Zap,
  Rocket,
  Settings,
  X,
  GripVertical,
  LayoutGrid,
  List,
} from "lucide-react";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";

import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import SuccessPopup from "../../components/SuccessPopup";
import { EmptyStateContainer } from "../../components/EmptyStateContainer";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import { CustomTextArea } from "../../components/CustomTextArea";
import { Label } from "../../components/Label";
import { UnifiedModal } from "../../components/UnifiedModal";
import { ConfirmModal } from "../../components/ConfirmModal";
import { Badge } from "../../components/Badge";
import { ViewModeSelector } from "../../components/ViewModeSelector";
import { DataTableToolbar, toolbarViewModeButtonSx } from "../../components/DataTable";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/Card";
import {
  getDropPosition,
  getFinishIndex,
  mountDragPreview,
  reindexByOrder,
} from "../../utils/drag_drop/dragDropUtils";
import { useDragAutoScroll } from "../../utils/drag_drop/useDragAutoScroll";
import {
  CUSTOM_PROJECTS_TUTORIAL_PARAM,
  CUSTOM_PROJECTS_TUTORIAL_VALUE,
  CUSTOM_PROJECTS_TUTORIAL_PHASE_PARAM,
  CUSTOM_PROJECTS_TUTORIAL_STEP_PARAM,
  CUSTOM_PROJECTS_TUTORIAL_PHASES,
  CUSTOM_PROJECTS_TEMPLATES_OVERVIEW_STEPS,
  CUSTOM_PROJECTS_TEMPLATE_MODAL_SETUP_STEPS,
  CUSTOM_PROJECTS_TEMPLATE_MODAL_EXAMPLE_STEPS,
  CUSTOM_PROJECTS_TUTORIAL_TEMPLATE_CARD,
  CUSTOM_PROJECTS_TUTORIAL_TEMPLATE_FORM,
  CUSTOM_PROJECTS_OVERVIEW_STEPS,
  getCustomProjectsPhaseSteps,
} from "../../Tutorials/projects/customProjectsTutorial";
import {
  TutorialJoyrideTooltip,
  getTutorialJoyrideStyles,
  TUTORIAL_FLOATER_PROPS,
  useTutorialsEnabled,
} from "../../Tutorials/index.js";
import { TutorialTargetProxies } from "../../Tutorials/TutorialTargetProxies.jsx";
import {
  prepareTutorialStep,
  runWhenTutorialTargetReady,
  withTutorialProxyTargets,
} from "../../Tutorials/tutorialTargets.js";
import { ProjectTemplatesTableView } from "./components/ProjectTemplatesTableView";

// Modal del tutorial: sin trampa de foco, que si no le roba el foco al tooltip de Joyride.
const TUTORIAL_DIALOG_PROPS = {
  disableEnforceFocus: true,
  disableAutoFocus: true,
  disableRestoreFocus: true,
};
const getTutorialTemplateRowProps = (template) =>
  template.id === CUSTOM_PROJECTS_TUTORIAL_TEMPLATE_CARD.id
    ? { "data-tutorial": "tutorial-template-row" }
    : {};
const noopTutorialHandler = () => undefined;

const INVALID_URL_MESSAGE =
  "Por favor, introduce una url válida (Ejemplo: https://miweb.com)";

const TEMPLATES_PAGE_SIZE = 40;
const TEMPLATES_TABLE_DEFAULT_PAGE_SIZE = 25;
const VIEW_MODE_OPTIONS = [
  { value: "cards", label: "Tarjetas", icon: LayoutGrid },
  { value: "table", label: "Tabla", icon: List },
];
const SORT_DIRECTION_OPTIONS = [
  { value: "asc", label: "Ascendente" },
  { value: "desc", label: "Descendente" },
];
const SORT_FIELD_OPTIONS = [{ value: "created_at", label: "Fecha de creación" }];

const formatDate = (value) => {
  if (!value) return "Sin fecha";
  try {
    return new Date(value).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

const getTemplateBadgeInfo = (template) => {
  if (template.agency) {
    return templateTypeBadgeColors.agency_custom;
  }

  if (template.template_type?.toLowerCase() === "recurrent_monthly") {
    return templateTypeBadgeColors.recurrent_monthly;
  }

  if (template.template_type?.toLowerCase() === "setup") {
    return templateTypeBadgeColors.setup;
  }

  return templateTypeBadgeColors.global_custom;
};

const extractErrorMessage = (error) => {
  if (error?.response?.data) {
    const data = error.response.data;

    // Verificar si hay errores específicos de URL del backend
    if (typeof data === "object" && !Array.isArray(data)) {
      // Buscar el error específico "Enter a valid URL"
      const hasUrlError = Object.keys(data).some((key) => {
        const value = data[key];
        if (Array.isArray(value)) {
          return value.some(
            (err) =>
              typeof err === "string" &&
              err.toLowerCase().includes("enter a valid url")
          );
        }
        if (typeof value === "string") {
          return value.toLowerCase().includes("enter a valid url");
        }
        return false;
      });

      if (hasUrlError) {
        return INVALID_URL_MESSAGE;
      }
    }

    if (typeof data === "string") {
      return data;
    }

    if (Array.isArray(data)) {
      return data.join(", ");
    }

    if (data.detail) {
      return data.detail;
    }

    const [firstKey] = Object.keys(data);
    if (firstKey) {
      const value = data[firstKey];
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      if (typeof value === "string") {
        return value;
      }
    }
  }

  return "Ocurrió un error. Intenta nuevamente.";
};

const buildEmptyPhase = (order, onlyTasks = false) => ({
  id: null,
  tempId: `phase-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)
    }`,
  name: onlyTasks ? "Tareas" : `Fase ${order}`,
  description: "",
  icon: "tabler-target",
  initial_state: "not_started",
  order,
  only_tasks: onlyTasks,
  tasks: [],
});

const buildEmptyTask = (order) => ({
  id: null,
  tempId: `task-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)
    }`,
  name: `Tarea ${order}`,
  description: "",
  initial_state: "planeado",
  documentation_urls: [],
  days_to_deadline: "",
  order,
});

// Mapeo de iconos string a componentes React
const iconMap = {
  "tabler-target": Target,
  "tabler-megaphone": Megaphone,
  "tabler-globe": Globe,
  "tabler-share": Share2,
  "tabler-pen": PenTool,
  "tabler-zap": Zap,
  "tabler-rocket": Rocket,
  "tabler-settings": Settings,
};

const getIconComponent = (iconString) => {
  return iconMap[iconString] || Target;
};

// Configuraciones de transición (igual que ManagementPhaseCard)
const transitionConfigs = {
  medium: {
    duration: 300,
    easing: "cubic-bezier(0.4, 0, 0.2, 1)",
  },
  content: {
    duration: 200,
    easing: "cubic-bezier(0.4, 0, 0.2, 1)",
    delay: 100,
  },
};

const transitionStyles = {
  chevron: {
    transition: `transform 200ms ${transitionConfigs.medium.easing}`,
  },
  card: {
    transition: `all ${transitionConfigs.medium.duration}ms ${transitionConfigs.medium.easing}`,
  },
  cardHover: {
    transition: `background-color 200ms ${transitionConfigs.medium.easing}`,
  },
  content: {
    transition: `opacity ${transitionConfigs.content.duration}ms ${transitionConfigs.content.easing} ${transitionConfigs.content.delay}ms`,
  },
};

// Componente para editar campos inline
const EditableField = ({
  value,
  onSave,
  placeholder,
  multiline = false,
  disabled = false,
  containerSx = {},
  textSx = {},
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  React.useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Box
        data-prevent-expand="true"
        onClick={(event) => event.stopPropagation()}
        sx={{
          display: "flex",
          alignItems: multiline ? "flex-start" : "center",
          gap: 1,
          width: "100%",
          cursor: "default",
          padding: 0,
          margin: 0,
        }}
      >
        {multiline ? (
          <CustomTextArea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                handleCancel();
              }
            }}
            autoFocus
            placeholder={placeholder}
            rows={2}
            sx={{
              flex: 1,
              width: "100%",
              resize: "vertical",
            }}
          />
        ) : (
          <CustomTextField
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              } else if (e.key === "Escape") {
                handleCancel();
              }
            }}
            autoFocus
            placeholder={placeholder}
            sx={{ flex: 1 }}
          />
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.5,
            flexShrink: 0,
            paddingTop: multiline ? "8px" : 0,
          }}
          data-prevent-expand="true"
        >
          <IconButton
            size="small"
            data-prevent-expand="true"
            onClick={(event) => {
              event.stopPropagation();
              handleSave();
            }}
          >
            <CheckCircle2 size={16} />
          </IconButton>
          <IconButton
            size="small"
            data-prevent-expand="true"
            onClick={(event) => {
              event.stopPropagation();
              handleCancel();
            }}
          >
            <X size={16} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-prevent-expand="true"
      sx={{
        display: "inline-flex",
        alignItems: multiline ? "flex-start" : "center",
        gap: 1,
        width: containerSx?.width || "fit-content",
        maxWidth: containerSx?.maxWidth || "100%",
        cursor: disabled ? "default" : "pointer",
        "&:hover": {
          backgroundColor: disabled ? "transparent" : "action.hover",
        },
        ...(containerSx?.padding === undefined && { padding: "4px 8px" }),
        borderRadius: "4px",
        ...containerSx,
      }}
      onClick={(event) => {
        if (disabled) {
          return;
        }
        event.stopPropagation();
        setIsEditing(true);
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
        {value || placeholder ? (
          <Typography
            variant={multiline ? "body2" : "body1"}
            sx={{
              color: value ? "text.primary" : "text.secondary",
              whiteSpace: multiline ? "pre-wrap" : "nowrap",
              overflow: multiline ? "visible" : "hidden",
              textOverflow: multiline ? "clip" : "ellipsis",
              wordBreak: multiline ? "break-word" : "normal",
              ...textSx,
            }}
          >
            {value || placeholder}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: "text.disabled",
              fontStyle: "italic",
              ...textSx,
            }}
          >
            {placeholder || "Sin valor"}
          </Typography>
        )}
      </Box>
      {!disabled && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
          sx={{
            opacity: 0.6,
            "&:hover": { opacity: 1 },
            flexShrink: 0,
            alignSelf: multiline ? "flex-start" : "center",
          }}
          data-prevent-expand="true"
        >
          <Edit size={14} />
        </IconButton>
      )}
    </Box>
  );
};

// Componente para tarea editable inline
const EditableTaskItem = ({
  task,
  phaseTempId,
  onTaskChange,
  onRemoveTask,
  onTaskDragStart,
  onTaskDragUpdate,
  onTaskDragLeave,
  onTaskDrop,
  onDragMove,
  onDragEnd,
  isDragging = false,
  dropIndicatorPosition = null,
  dndEnabled = true,
  disabled = false,
  urlError = null, // Error de validación de URL
}) => {
  const theme = useTheme();
  const itemRef = useRef(null);
  const dragHandleRef = useRef(null);

  useEffect(() => {
    if (disabled || !dndEnabled) {
      return undefined;
    }

    const element = itemRef.current;
    const dragHandle = dragHandleRef.current;
    if (!element || !dragHandle) {
      return undefined;
    }

    return combine(
      draggable({
        element,
        dragHandle,
        getInitialData: () => ({
          entityType: "task",
          phaseTempId,
          taskTempId: task.tempId,
        }),
        onGenerateDragPreview: ({ nativeSetDragImage, location, source }) => {
          mountDragPreview({
            nativeSetDragImage,
            element: source.element,
            input: location.current.input,
          });
        },
        onDragStart: () => {
          onTaskDragStart?.(phaseTempId, task.tempId);
        },
        onDrag: ({ location }) => {
          const clientY = location.current.input?.clientY;
          if (typeof clientY === "number") {
            onDragMove?.(clientY);
          }
        },
        onDrop: () => {
          onDragEnd?.();
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) =>
          source.data?.entityType === "task" &&
          source.data?.phaseTempId === phaseTempId &&
          source.data?.taskTempId !== task.tempId,
        getData: ({ input, element: currentElement }) => ({
          entityType: "task",
          phaseTempId,
          taskTempId: task.tempId,
          position: getDropPosition({ input, element: currentElement }),
        }),
        onDragEnter: ({ self }) => {
          if (self.data?.position === "before" || self.data?.position === "after") {
            onTaskDragUpdate?.(phaseTempId, task.tempId, self.data.position);
          }
        },
        onDrag: ({ self }) => {
          if (self.data?.position === "before" || self.data?.position === "after") {
            onTaskDragUpdate?.(phaseTempId, task.tempId, self.data.position);
          }
        },
        onDragLeave: () => {
          onTaskDragLeave?.(phaseTempId, task.tempId);
        },
        onDrop: ({ source, self }) => {
          if (self.data?.position !== "before" && self.data?.position !== "after") {
            return;
          }

          const sourcePhaseTempId = source.data?.phaseTempId;
          const sourceTaskTempId = source.data?.taskTempId;
          if (
            typeof sourcePhaseTempId !== "string" ||
            typeof sourceTaskTempId !== "string"
          ) {
            return;
          }

          onTaskDrop?.({
            sourcePhaseTempId,
            sourceTaskTempId,
            targetPhaseTempId: phaseTempId,
            targetTaskTempId: task.tempId,
            position: self.data.position,
          });
        },
      })
    );
  }, [
    disabled,
    dndEnabled,
    onDragEnd,
    onDragMove,
    onTaskDragLeave,
    onTaskDragStart,
    onTaskDragUpdate,
    onTaskDrop,
    phaseTempId,
    task.tempId,
  ]);

  return (
    <Stack
      ref={itemRef}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={3}
      sx={{
        position: "relative",
        padding: "16px",
        backgroundColor: `${theme.palette.action.hover}30`,
        borderRadius: "8px",
        border: `1px solid ${urlError ? theme.palette.error.main : theme.palette.divider
          }`,
        opacity: isDragging ? 0.48 : 1,
        ...(dropIndicatorPosition === "before" && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            top: -6,
            height: "3px",
            borderRadius: "999px",
            backgroundColor: theme.palette.primary.main,
          },
        }),
        ...(dropIndicatorPosition === "after" && {
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: -6,
            height: "3px",
            borderRadius: "999px",
            backgroundColor: theme.palette.primary.main,
          },
        }),
        flexWrap: { xs: "wrap", sm: "nowrap" },
        rowGap: { xs: 2, sm: 0 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={3}
        sx={{ minWidth: 0, flex: 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {!disabled && dndEnabled && (
            <Box
              ref={dragHandleRef}
              data-prevent-expand="true"
              onClick={(event) => event.stopPropagation()}
              sx={{
                display: "flex",
                alignItems: "center",
                color: theme.palette.text.secondary,
                cursor: "grab",
                mr: 1,
                "&:active": { cursor: "grabbing" },
              }}
            >
              <GripVertical size={16} />
            </Box>
          )}
          <Settings size={20} color={theme.palette.text.secondary} />
        </Box>

        <Stack
          direction="column"
          spacing={0}
          sx={{ minWidth: 0, maxWidth: "100%", flex: 1 }}
        >
          <EditableField
            value={task.name}
            onSave={(value) =>
              onTaskChange(phaseTempId, task.tempId, "name", value)
            }
            placeholder="Nombre de la tarea"
            disabled={disabled}
            containerSx={{
              padding: 0,
              width: "fit-content",
              maxWidth: "100%",
              "&:hover": {
                backgroundColor: disabled
                  ? "transparent"
                  : theme.palette.action.hover,
              },
            }}
            textSx={{ fontWeight: 500, fontSize: "0.95rem" }}
          />
          <EditableField
            value={task.description}
            onSave={(value) =>
              onTaskChange(phaseTempId, task.tempId, "description", value)
            }
            placeholder="Descripción de la tarea"
            multiline
            disabled={disabled}
            containerSx={{
              padding: 0,
              width: "fit-content",
              maxWidth: "100%",
              mt: "2px",
              "&:hover": {
                backgroundColor: disabled
                  ? "transparent"
                  : theme.palette.action.hover,
              },
            }}
            textSx={{ fontSize: "0.8125rem", color: "text.secondary" }}
          />
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ flexShrink: 0, flexWrap: "wrap", rowGap: 1 }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            minWidth: 0,
            maxWidth: "250px",
          }}
        >
          {/* El detalle del error se muestra por campo dentro de LinkListEditor.
              Aquí solo se conserva el borde rojo de la tarea (ver `urlError` arriba). */}
          <LinkListEditor
            value={task.documentation_urls || []}
            disabled={disabled}
            onChange={(links) =>
              onTaskChange(phaseTempId, task.tempId, "documentation_urls", links)
            }
          />
        </Box>
        {!disabled && (
          <IconButton
            size="small"
            data-prevent-expand="true"
            onClick={(event) => {
              event.stopPropagation();
              onRemoveTask(phaseTempId, task.tempId);
            }}
            sx={{ color: "error.main" }}
          >
            <Trash2 size={16} />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

// Componente para fase editable con estilo ManagementPhaseCard
const EditablePhaseCard = ({
  phase,
  index,
  isExpanded,
  onToggleExpand,
  onPhaseChange,
  onAddTask,
  onRemovePhase,
  onPhaseDragStart,
  onPhaseDragUpdate,
  onPhaseDragLeave,
  onPhaseDrop,
  onPhaseHandleGrab,
  onDragMove,
  onDragEnd,
  isDragging = false,
  dropIndicatorPosition = null,
  dndEnabled = true,
  children,
  disabled = false,
}) => {
  const theme = useTheme();
  const [isHoveringInteractive, setIsHoveringInteractive] = useState(false);
  const PhaseIcon = getIconComponent(phase.icon);
  const hasExpandableContent = phase.tasks && phase.tasks.length > 0;
  const cardContainerRef = useRef(null);
  const dragHandleRef = useRef(null);

  useEffect(() => {
    if (disabled || !dndEnabled) {
      return undefined;
    }

    const element = cardContainerRef.current;
    const dragHandle = dragHandleRef.current;
    if (!element || !dragHandle) {
      return undefined;
    }

    return combine(
      draggable({
        element,
        dragHandle,
        getInitialData: () => ({
          entityType: "phase",
          phaseTempId: phase.tempId,
        }),
        onGenerateDragPreview: ({ nativeSetDragImage, location, source }) => {
          mountDragPreview({
            nativeSetDragImage,
            element: source.element,
            input: location.current.input,
          });
        },
        onDragStart: () => {
          onPhaseDragStart?.(phase.tempId);
        },
        onDrag: ({ location }) => {
          const clientY = location.current.input?.clientY;
          if (typeof clientY === "number") {
            onDragMove?.(clientY);
          }
        },
        onDrop: () => {
          onDragEnd?.();
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) =>
          source.data?.entityType === "phase" &&
          source.data?.phaseTempId !== phase.tempId,
        getData: ({ input, element: currentElement }) => ({
          entityType: "phase",
          phaseTempId: phase.tempId,
          position: getDropPosition({ input, element: currentElement }),
        }),
        onDragEnter: ({ self }) => {
          if (self.data?.position === "before" || self.data?.position === "after") {
            onPhaseDragUpdate?.(phase.tempId, self.data.position);
          }
        },
        onDrag: ({ self }) => {
          if (self.data?.position === "before" || self.data?.position === "after") {
            onPhaseDragUpdate?.(phase.tempId, self.data.position);
          }
        },
        onDragLeave: () => {
          onPhaseDragLeave?.(phase.tempId);
        },
        onDrop: ({ source, self }) => {
          if (self.data?.position !== "before" && self.data?.position !== "after") {
            return;
          }

          const sourcePhaseTempId = source.data?.phaseTempId;
          if (typeof sourcePhaseTempId !== "string") {
            return;
          }

          onPhaseDrop?.({
            sourcePhaseTempId,
            targetPhaseTempId: phase.tempId,
            position: self.data.position,
          });
        },
      })
    );
  }, [
    disabled,
    dndEnabled,
    onDragEnd,
    onDragMove,
    onPhaseDragLeave,
    onPhaseDragStart,
    onPhaseDragUpdate,
    onPhaseDrop,
    phase.tempId,
  ]);

  const handleHeaderClick = (event) => {
    if (!hasExpandableContent) {
      return;
    }

    const target = event.target;
    if (target instanceof Element) {
      const interactiveAncestor = target.closest(
        "[data-prevent-expand='true']"
      );
      if (interactiveAncestor) {
        return;
      }
    }

    onToggleExpand();
  };

  return (
    <Box
      ref={cardContainerRef}
      sx={{
        position: "relative",
        opacity: isDragging ? 0.48 : 1,
        ...(dropIndicatorPosition === "before" && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            top: -6,
            height: "3px",
            borderRadius: "999px",
            backgroundColor: theme.palette.primary.main,
            zIndex: 2,
          },
        }),
        ...(dropIndicatorPosition === "after" && {
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: -6,
            height: "3px",
            borderRadius: "999px",
            backgroundColor: theme.palette.primary.main,
            zIndex: 2,
          },
        }),
      }}
    >
      <Card
        sx={{
          overflow: "hidden",
          ...transitionStyles.card,
          transform: isExpanded ? "translateY(-1px)" : "translateY(0px)",
          boxShadow: isExpanded ? theme.shadows[2] : theme.shadows[1],
          "&:hover": {
            boxShadow: isExpanded ? theme.shadows[3] : theme.shadows[2],
            transform: isExpanded ? "translateY(-1px)" : "translateY(-0.5px)",
          },
        }}
      >
      <Box
        onClick={handleHeaderClick}
        onKeyDown={(event) => {
          if (!hasExpandableContent) {
            return;
          }

          if (event.target instanceof Element) {
            const interactiveAncestor = event.target.closest(
              "[data-prevent-expand='true']"
            );
            if (interactiveAncestor) {
              return;
            }
          }

          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleHeaderClick(event);
          }
        }}
        role={hasExpandableContent ? "button" : undefined}
        tabIndex={hasExpandableContent ? 0 : -1}
        onMouseMove={(event) => {
          if (!hasExpandableContent) {
            return;
          }

          const interactiveAncestor = event.target.closest(
            "[data-prevent-expand='true']"
          );
          setIsHoveringInteractive(Boolean(interactiveAncestor));
        }}
        onMouseLeave={() => {
          setIsHoveringInteractive(false);
        }}
        sx={(theme) => ({
          cursor: hasExpandableContent ? "pointer" : "default",
          ...transitionStyles.cardHover,
          "&:hover": {
            backgroundColor: isHoveringInteractive
              ? "transparent"
              : hasExpandableContent
                ? theme.palette.action.hover
                : "transparent",
          },
          "&:focus-visible": {
            outline: hasExpandableContent
              ? `2px solid ${theme.palette.primary.main}`
              : "none",
            outlineOffset: "-2px",
          },
        })}
      >
        <CardHeader sx={{ pb: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ flex: 1, minWidth: 0 }}
            >
              {!disabled && dndEnabled && (
                <Box
                  ref={dragHandleRef}
                  data-prevent-expand="true"
                  onMouseDown={(event) => {
                    event.stopPropagation();
                    onPhaseHandleGrab?.();
                  }}
                  onTouchStart={(event) => {
                    event.stopPropagation();
                    onPhaseHandleGrab?.();
                  }}
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: theme.palette.text.secondary,
                    cursor: "grab",
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                  <GripVertical size={18} />
                </Box>
              )}

              {hasExpandableContent && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    ...transitionStyles.chevron,
                    transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                    flexShrink: 0,
                  }}
                >
                  <ChevronDown size={20} color={theme.palette.text.secondary} />
                </Box>
              )}

              <Box
                sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
              >
                <PhaseIcon size={24} color={theme.palette.primary.main} />
              </Box>

              <Stack
                direction="column"
                spacing={0}
                sx={{ flex: 1, minWidth: 0, maxWidth: "100%" }}
              >
                <EditableField
                  value={phase.name}
                  onSave={(value) => onPhaseChange(phase.tempId, "name", value)}
                  placeholder={`Fase ${index + 1}`}
                  disabled={disabled}
                  containerSx={{
                    padding: 0,
                    width: "fit-content",
                    maxWidth: "100%",
                    "&:hover": {
                      backgroundColor: disabled
                        ? "transparent"
                        : theme.palette.action.hover,
                    },
                  }}
                  textSx={{ fontSize: "1.125rem", fontWeight: 600 }}
                />
                <EditableField
                  value={phase.description}
                  onSave={(value) =>
                    onPhaseChange(phase.tempId, "description", value)
                  }
                  placeholder="Descripción de la fase"
                  multiline
                  disabled={disabled}
                  containerSx={{
                    padding: 0,
                    width: "fit-content",
                    maxWidth: "100%",
                    mt: "2px",
                    "&:hover": {
                      backgroundColor: disabled
                        ? "transparent"
                        : theme.palette.action.hover,
                    },
                  }}
                  textSx={{ fontSize: "1rem", color: "text.secondary" }}
                />
              </Stack>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              spacing={3}
              onMouseEnter={() => setIsHoveringInteractive(true)}
              onMouseLeave={() => setIsHoveringInteractive(false)}
              data-prevent-expand="true"
              sx={{ flexShrink: 0 }}
            >
              {!disabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddTask(phase.tempId);
                  }}
                  sx={{
                    fontSize: "0.875rem",
                    padding: "6px 12px",
                  }}
                  data-prevent-expand="true"
                >
                  <Plus size={14} style={{ marginRight: "6px" }} />
                  Añadir tarea
                </Button>
              )}
              {!disabled && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemovePhase(phase.tempId);
                  }}
                  sx={{ color: "error.main" }}
                  data-prevent-expand="true"
                >
                  <Trash2 size={16} />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </CardHeader>
      </Box>

      {/* Expandable content */}
      {hasExpandableContent && (
        <Collapse
          data-drag-preview-hidden="true"
          in={isExpanded}
          timeout={transitionConfigs.medium.duration}
          easing={{
            enter: transitionConfigs.medium.easing,
            exit: transitionConfigs.medium.easing,
          }}
        >
          <CardContent sx={{ pt: 0, pb: 2 }}>
            <Box
              sx={{
                ml: 8,
                opacity: isExpanded ? 1 : 0,
                ...transitionStyles.content,
              }}
            >
              {children}
            </Box>
          </CardContent>
        </Collapse>
      )}
      </Card>
    </Box>
  );
};

const TemplateCard = ({ template, onEdit, onDelete }) => {
  const theme = useTheme();
  const description = template.description || "Sin descripción";
  const canEdit = template.can_edit || false;
  const isSetupTemplate = template.template_type?.toLowerCase() === "setup";
  const badgeInfo = getTemplateBadgeInfo(template);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minWidth: 0,
        borderRadius: "12px",
      }}
    >
      <CardContent
        sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography
              component="h3"
              sx={{
                fontSize: "1.05rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {template.name}
            </Typography>
            <Badge
              sx={{
                backgroundColor: badgeInfo.background,
                color: badgeInfo.text,
                border: `1px solid ${badgeInfo.border}`,
              }}
            >
              {badgeInfo.label}
            </Badge>
          </Stack>

          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mt: 0.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.5,
            }}
          >
            {description}
          </Typography>

          {(template.phases_count !== undefined || template.tasks_count !== undefined) && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                mt: 1,
                fontWeight: 400,
              }}
            >
              ({template.phases_count || 0} Fases / {template.tasks_count || 0} Tareas)
            </Typography>
          )}

          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              mt: 1,
              display: "block",
            }}
          >
            {`Actualizada el ${formatDate(template.updated_at)}`}
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          sx={{ justifyContent: "flex-end", mt: 2, flexWrap: "wrap", rowGap: 1 }}
        >
          <Button
            variant="outline"
            size="sm"
            sx={{ minWidth: 0 }}
            onClick={() => onEdit(template)}
          >
            <Edit size={16} style={{ marginRight: "6px" }} />
            {canEdit ? "Editar" : "Ver"}
          </Button>
          {canEdit && !isSetupTemplate && (
            <Button
              variant="outline"
              size="sm"
              sx={{ minWidth: 0 }}
              onClick={() => onDelete(template)}
              color="error"
            >
              <Trash2 size={16} style={{ marginRight: "6px" }} />
              Eliminar
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export const TemplateEditorModal = ({
  open,
  onClose,
  initialData,
  mode,
  onSaved,
  canEdit,
  agencyId,
  isSuperAdmin = false,
  titleOverride = null,
  submitLabelOverride = null,
  initialName = "",
  initialDescription = "",
  onSubmitOverride = null,
  tutorialExampleData = null,
  disableBackdropClose = false,
  dialogProps = null,
}) => {
  const theme = useTheme();
  const isProjectDefinitionMode = typeof onSubmitOverride === "function";
  const [creationMode, setCreationMode] = useState(null); // null | 'phases' | 'tasks'
  const [formState, setFormState] = useState({
    name: "",
    description: "",
    is_active: true,
    phases: [],
    agency: agencyId || null,
  });
  const [formError, setFormError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState(new Set());
  const [urlErrors, setUrlErrors] = useState({}); // Errores de validación de URLs por tarea
  const [isFormReady, setIsFormReady] = useState(mode === "create");
  const [draggedPhaseTempId, setDraggedPhaseTempId] = useState(null);
  const [draggedTaskRef, setDraggedTaskRef] = useState(null);
  const [phaseDropIndicator, setPhaseDropIndicator] = useState(null);
  const [taskDropIndicator, setTaskDropIndicator] = useState(null);
  const {
    scrollContainerRef,
    handleDragMoveAutoScroll,
    stopAutoScroll,
  } = useDragAutoScroll();

  const resolveTemplateAgencyId = (template) => {
    if (!template) return null;
    if (template.agency_id) return template.agency_id;
    if (typeof template.agency === "object" && template.agency !== null) {
      return template.agency.id ?? null;
    }
    if (typeof template.agency === "number") {
      return template.agency;
    }
    if (typeof template.agency === "string") {
      const numericValue = Number(template.agency);
      return Number.isNaN(numericValue) ? template.agency : numericValue;
    }
    return null;
  };

  const agencyLabel = useMemo(() => {
    if (initialData?.template_type === "setup") {
      return "Plantilla global (Setup)";
    }

    const fromTemplate =
      initialData?.agency_name ||
      initialData?.agency?.name ||
      initialData?.agency_name_display;

    if (fromTemplate) {
      return fromTemplate;
    }

    // If no agency (global custom template)
    if (!initialData?.agency) {
      return "Plantilla global";
    }

    return "Sin agencia";
  }, [initialData]);

  useEffect(() => {
    if (!open) {
      stopAutoScroll();
      setFormError("");
      setExpandedPhases(new Set());
      setUrlErrors({}); // Limpiar errores de URL cuando se cierra el modal
      setDraggedPhaseTempId(null);
      setDraggedTaskRef(null);
      setPhaseDropIndicator(null);
      setTaskDropIndicator(null);
      setCreationMode(null);
      return;
    }

    if (mode !== "create") {
      if (!initialData) {
        setIsFormReady(false);
        return;
      }
      const phases = (initialData.template_phases || [])
        .map((phase) => ({
          id: phase.id,
          tempId: String(phase.id),
          name: phase.name,
          description: phase.description || "",
          icon: phase.icon || "tabler-target",
          initial_state: phase.initial_state || "not_started",
          order: phase.order,
          only_tasks: phase.only_tasks || false,
          tasks: (phase.template_tasks || [])
            .map((task) => ({
              id: task.id,
              tempId: String(task.id),
              name: task.name,
              description: task.description || "",
              initial_state: task.initial_state || "planeado",
              documentation_urls: task.documentation_urls || [],
              days_to_deadline: task.days_to_deadline ?? "",
              order: task.order,
            }))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        }))
        .sort((a, b) => a.order - b.order);

      // Detectar el modo según los datos existentes
      const hasOnlyTasksPhase = phases.length > 0 && phases[0].only_tasks;
      setCreationMode(hasOnlyTasksPhase ? 'tasks' : 'phases');

      setFormState({
        name: initialData.name || "",
        description: initialData.description || "",
        is_active: initialData.is_active ?? true,
        phases,
        agency: resolveTemplateAgencyId(initialData),
      });
      // Las fases comienzan cerradas por defecto
      setIsFormReady(true);
      return;
    }

    // Modo create: no inicializar fases, esperar a que el usuario elija el modo
    setFormState({
      name: initialName || "",
      description: initialDescription || "",
      is_active: true,
      phases: [],
      agency: agencyId || null,
    });
    setCreationMode(null);
    setIsFormReady(true);
  }, [agencyId, initialData, initialDescription, initialName, mode, open, stopAutoScroll]);

  // ── Tutorial: inject example data when tutorialExampleData prop is set ──────
  // Declarado tras el efecto de apertura para sobrescribir su reset en el mismo commit.
  const tutorialExampleInjectedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      tutorialExampleInjectedRef.current = false;
      return;
    }
    if (!tutorialExampleData) {
      // Al retroceder en el tutorial se vuelve al formulario vacío con el selector de estructura.
      if (tutorialExampleInjectedRef.current) {
        tutorialExampleInjectedRef.current = false;
        setFormState((prev) => ({ ...prev, name: "", description: "", phases: [] }));
        setCreationMode(null);
        setExpandedPhases(new Set());
      }
      return;
    }
    tutorialExampleInjectedRef.current = true;
    const phasesWithTempIds = tutorialExampleData.phases.map((phase) => ({
      ...phase,
      tempId: `tutorial-phase-${phase.order}`,
      tasks: phase.tasks.map((task) => ({
        ...task,
        tempId: `tutorial-task-${phase.order}-${task.order}`,
      })),
    }));
    setFormState((prev) => ({
      ...prev,
      name: tutorialExampleData.name,
      description: tutorialExampleData.description,
      phases: phasesWithTempIds,
    }));
    setCreationMode(tutorialExampleData.creationMode);
    // Solo la primera fase desplegada para que el bloque quepa en el modal.
    setExpandedPhases(new Set(phasesWithTempIds.slice(0, 1).map((p) => p.tempId)));
  }, [open, tutorialExampleData]);
  // ─────────────────────────────────────────────────────────────────────────────

  // Setup templates: only the name field should be non-editable
  const isSetupTemplate = initialData?.template_type?.toLowerCase() === "setup";
  const isEditable = canEdit;
  const dndEnabled = mode === "edit" && Boolean(initialData?.id);

  const handlePhaseChange = (tempId, key, value) => {
    setFormState((prev) => ({
      ...prev,
      phases: prev.phases.map((phase) =>
        phase.tempId === tempId ? { ...phase, [key]: value } : phase
      ),
    }));
  };

  const handleTaskChange = (phaseTempId, taskTempId, key, value) => {
    let newValue = value;
    // Validar URL en tiempo real si el campo es documentation_urls
    if (key === "documentation_urls") {
      const errorKey = `${phaseTempId}_${taskTempId}`;
      const urlError = (linksAreValid(newValue) ? null : INVALID_URL_MESSAGE);

      setUrlErrors((prev) => {
        const newErrors = { ...prev };
        if (urlError) {
          newErrors[errorKey] = urlError;
        } else {
          delete newErrors[errorKey];
        }
        return newErrors;
      });
    }

    setFormState((prev) => ({
      ...prev,
      phases: prev.phases.map((phase) =>
        phase.tempId === phaseTempId
          ? {
            ...phase,
            tasks: phase.tasks.map((task) =>
              task.tempId === taskTempId ? { ...task, [key]: newValue } : task
            ),
          }
          : phase
      ),
    }));
  };

  const handleAddPhase = () => {
    const newPhase = buildEmptyPhase(formState.phases.length + 1, false);
    setFormState((prev) => ({
      ...prev,
      phases: [...prev.phases, newPhase],
    }));
    // Expandir automáticamente la nueva fase
    setExpandedPhases((prev) => new Set([...prev, newPhase.tempId]));
  };

  const handleSelectCreationMode = (mode) => {
    setCreationMode(mode);
    if (mode === 'tasks') {
      // Crear una fase contenedora con only_tasks=true y una tarea por defecto (idéntico a fases)
      const tasksPhase = buildEmptyPhase(1, true);
      tasksPhase.tasks = [buildEmptyTask(1)];
      setFormState((prev) => ({
        ...prev,
        phases: [tasksPhase],
      }));
      setExpandedPhases(new Set([tasksPhase.tempId]));
    } else {
      // Crear una fase normal
      const normalPhase = buildEmptyPhase(1, false);
      setFormState((prev) => ({
        ...prev,
        phases: [normalPhase],
      }));
      setExpandedPhases(new Set());
    }
  };

  const handleRemovePhase = (tempId) => {
    // Limpiar errores de URL de todas las tareas de la fase
    setUrlErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach((errorKey) => {
        if (errorKey.startsWith(`${tempId}_`)) {
          delete newErrors[errorKey];
        }
      });
      return newErrors;
    });

    setFormState((prev) => ({
      ...prev,
      phases: reindexByOrder(
        prev.phases.filter((phase) => phase.tempId !== tempId)
      ),
    }));
  };

  const handleAddTask = (phaseTempId) => {
    setFormState((prev) => ({
      ...prev,
      phases: prev.phases.map((phase) =>
        phase.tempId === phaseTempId
          ? {
            ...phase,
            tasks: [...phase.tasks, buildEmptyTask(phase.tasks.length + 1)],
          }
          : phase
      ),
    }));
    // Expandir automáticamente la fase si no está expandida
    if (!expandedPhases.has(phaseTempId)) {
      setExpandedPhases((prev) => new Set([...prev, phaseTempId]));
    }
  };

  const handleRemoveTask = (phaseTempId, taskTempId) => {
    // Limpiar error de URL si existe
    const errorKey = `${phaseTempId}_${taskTempId}`;
    setUrlErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });

    setFormState((prev) => ({
      ...prev,
      phases: prev.phases.map((phase) =>
        phase.tempId === phaseTempId
          ? {
            ...phase,
            tasks: reindexByOrder(
              phase.tasks.filter((task) => task.tempId !== taskTempId)
            ),
          }
          : phase
      ),
    }));
  };

  const clearDragState = () => {
    stopAutoScroll();
    setDraggedPhaseTempId(null);
    setDraggedTaskRef(null);
    setPhaseDropIndicator(null);
    setTaskDropIndicator(null);
  };

  const handlePhaseDragStart = (phaseTempId) => {
    setExpandedPhases(new Set());
    setDraggedPhaseTempId(phaseTempId);
    setPhaseDropIndicator(null);
  };

  const handlePhaseHandleGrab = () => {
    setExpandedPhases(new Set());
  };

  const handlePhaseDragUpdate = (phaseTempId, position) => {
    if (position !== "before" && position !== "after") {
      return;
    }
    setPhaseDropIndicator({ phaseTempId, position });
  };

  const handlePhaseDragLeave = (phaseTempId) => {
    setPhaseDropIndicator((prev) =>
      prev?.phaseTempId === phaseTempId ? null : prev
    );
  };

  const handlePhaseDrop = ({
    sourcePhaseTempId,
    targetPhaseTempId,
    position,
  }) => {
    if (position !== "before" && position !== "after") {
      clearDragState();
      return;
    }

    setFormState((prev) => {
      const startIndex = prev.phases.findIndex(
        (phase) => phase.tempId === sourcePhaseTempId
      );
      const targetIndex = prev.phases.findIndex(
        (phase) => phase.tempId === targetPhaseTempId
      );

      if (startIndex === -1 || targetIndex === -1 || startIndex === targetIndex) {
        return prev;
      }

      const finishIndex = getFinishIndex({
        startIndex,
        targetIndex,
        position,
      });
      if (finishIndex === startIndex) {
        return prev;
      }

      const phases = reorder({
        list: prev.phases,
        startIndex,
        finishIndex,
      });

      return {
        ...prev,
        phases: reindexByOrder(phases),
      };
    });

    clearDragState();
  };

  const handleTaskDragStart = (phaseTempId, taskTempId) => {
    setDraggedTaskRef({ phaseTempId, taskTempId });
    setTaskDropIndicator(null);
  };

  const handleTaskDragUpdate = (phaseTempId, taskTempId, position) => {
    if (position !== "before" && position !== "after") {
      return;
    }
    setTaskDropIndicator({ phaseTempId, taskTempId, position });
  };

  const handleTaskDragLeave = (phaseTempId, taskTempId) => {
    setTaskDropIndicator((prev) =>
      prev?.phaseTempId === phaseTempId && prev?.taskTempId === taskTempId
        ? null
        : prev
    );
  };

  const handleTaskDrop = ({
    sourcePhaseTempId,
    sourceTaskTempId,
    targetPhaseTempId,
    targetTaskTempId,
    position,
  }) => {
    if (position !== "before" && position !== "after") {
      clearDragState();
      return;
    }

    if (sourcePhaseTempId !== targetPhaseTempId) {
      clearDragState();
      return;
    }

    setFormState((prev) => {
      const phaseIndex = prev.phases.findIndex(
        (phase) => phase.tempId === targetPhaseTempId
      );
      if (phaseIndex === -1) {
        return prev;
      }

      const phase = prev.phases[phaseIndex];
      const startIndex = phase.tasks.findIndex(
        (task) => task.tempId === sourceTaskTempId
      );
      const targetIndex = phase.tasks.findIndex(
        (task) => task.tempId === targetTaskTempId
      );

      if (startIndex === -1 || targetIndex === -1 || startIndex === targetIndex) {
        return prev;
      }

      const finishIndex = getFinishIndex({
        startIndex,
        targetIndex,
        position,
      });
      if (finishIndex === startIndex) {
        return prev;
      }

      const reorderedTasks = reorder({
        list: phase.tasks,
        startIndex,
        finishIndex,
      });

      const phases = [...prev.phases];
      phases[phaseIndex] = {
        ...phase,
        tasks: reindexByOrder(reorderedTasks),
      };

      return {
        ...prev,
        phases,
      };
    });

    clearDragState();
  };

  const togglePhase = (phaseTempId) => {
    const newExpanded = new Set(expandedPhases);
    if (newExpanded.has(phaseTempId)) {
      newExpanded.delete(phaseTempId);
    } else {
      newExpanded.add(phaseTempId);
    }
    setExpandedPhases(newExpanded);
  };

  const validateForm = () => {
    if (!formState.name.trim()) {
      setFormError(
        isProjectDefinitionMode
          ? "El nombre del proyecto es obligatorio."
          : "El nombre de la plantilla es obligatorio."
      );
      return false;
    }

    for (const phase of formState.phases) {
      if (!phase.name.trim()) {
        setFormError("Todas las fases deben tener un nombre.");
        return false;
      }
      for (const task of phase.tasks) {
        if (!task.name.trim()) {
          setFormError("Todas las tareas deben tener un nombre.");
          return false;
        }

        if (!linksAreValid((task.documentation_urls || []))) {
          setFormError("Revisa los enlaces de las tareas.");
          return false;
        }

        // Validar enlaces de documentación

      }
    }

    setFormError("");
    setUrlErrors({});
    return true;
  };

  const saveTemplate = async () => {
    if (!isEditable) {
      onClose();
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      if (isProjectDefinitionMode) {
        await onSubmitOverride({
          name: formState.name.trim(),
          description: formState.description.trim(),
          phases: formState.phases.map((phase) => ({
            ...phase,
            tasks: phase.tasks.map((task) => ({
              ...task,
              documentation_urls: normalizeLinks(task.documentation_urls),
            })),
          })),
        });

        onSaved();
        onClose();
        return;
      }

      let templateId = initialData?.id || null;

      if (mode === "create") {
        // Backend automatically sets agency based on user role:
        // - SuperAdmin: agency = null (creates global custom template)
        // - Agency Admin: agency = user's agency (creates agency custom template)
        const response = await apiClient.post("projects/templates/", {
          name: formState.name.trim(),
          description: formState.description.trim(),
          template_type: "custom",
          // Do NOT send agency field - backend auto-sets it
          is_active: formState.is_active,
        });

        templateId = response.data.id;

        // Crear fases y tareas
        for (const [index, phase] of formState.phases.entries()) {
          const phaseResponse = await apiClient.post(
            "projects/template-phases/",
            {
              template: templateId,
              name: phase.name,
              description: phase.description,
              icon: phase.icon || "tabler-target",
              initial_state: phase.initial_state || "not_started",
              order: phase.order || index + 1,
              only_tasks: phase.only_tasks || false,
            }
          );

          const newPhaseId = phaseResponse.data.id;

          for (const [taskIndex, task] of phase.tasks.entries()) {

            await apiClient.post("projects/template-tasks/", {
              phase: newPhaseId,
              name: task.name,
              description: task.description,
              initial_state: task.initial_state || "planeado",
              documentation_urls: normalizeLinks(task.documentation_urls),
              days_to_deadline:
                task.days_to_deadline === ""
                  ? null
                  : Number(task.days_to_deadline),
              order: task.order || taskIndex + 1,
            });
          }
        }
      } else if (templateId) {
        // Actualizar datos generales del template
        const templateUpdates = {};
        if (formState.name !== initialData.name) {
          templateUpdates.name = formState.name;
        }
        if ((formState.description || "") !== (initialData.description || "")) {
          templateUpdates.description = formState.description;
        }
        if (formState.is_active !== initialData.is_active) {
          templateUpdates.is_active = formState.is_active;
        }

        if (Object.keys(templateUpdates).length > 0) {
          await apiClient.patch(
            `projects/templates/${templateId}/`,
            templateUpdates
          );
        }

        const originalPhases = initialData.template_phases || [];
        const originalPhaseMap = new Map(
          originalPhases.map((phase) => [phase.id, phase])
        );

        // Eliminar fases eliminadas
        const phasesToDelete = originalPhases.filter(
          (phase) => !formState.phases.some((item) => item.id === phase.id)
        );
        for (const phase of phasesToDelete) {
          await apiClient.delete(`projects/template-phases/${phase.id}/`);
        }

        // Procesar fases actuales
        for (const [index, phase] of formState.phases.entries()) {
          if (!phase.id) {
            // Nueva fase
            const phaseResponse = await apiClient.post(
              "projects/template-phases/",
              {
                template: templateId,
                name: phase.name,
                description: phase.description,
                icon: phase.icon || "tabler-target",
                initial_state: phase.initial_state || "not_started",
                order: phase.order || index + 1,
                only_tasks: phase.only_tasks || false,
              }
            );

            const newPhaseId = phaseResponse.data.id;

            for (const [taskIndex, task] of phase.tasks.entries()) {
              await apiClient.post("projects/template-tasks/", {
                phase: newPhaseId,
                name: task.name,
                description: task.description,
                initial_state: task.initial_state || "planeado",
                documentation_urls: normalizeLinks(task.documentation_urls),
                days_to_deadline:
                  task.days_to_deadline === ""
                    ? null
                    : Number(task.days_to_deadline),
                order: task.order || taskIndex + 1,
              });
            }
          } else {
            // Actualizar fase existente
            const originalPhase = originalPhaseMap.get(phase.id);
            if (!originalPhase) continue;

            const phaseUpdates = {};
            if (phase.name !== originalPhase.name) {
              phaseUpdates.name = phase.name;
            }
            if (
              (phase.description || "") !== (originalPhase.description || "")
            ) {
              phaseUpdates.description = phase.description;
            }
            if ((phase.icon || "") !== (originalPhase.icon || "")) {
              phaseUpdates.icon = phase.icon;
            }
            if (
              (phase.initial_state || "not_started") !==
              (originalPhase.initial_state || "not_started")
            ) {
              phaseUpdates.initial_state = phase.initial_state;
            }
            if ((phase.order || index + 1) !== originalPhase.order) {
              phaseUpdates.order = phase.order || index + 1;
            }
            if ((phase.only_tasks || false) !== (originalPhase.only_tasks || false)) {
              phaseUpdates.only_tasks = phase.only_tasks || false;
            }

            if (Object.keys(phaseUpdates).length > 0) {
              await apiClient.patch(
                `projects/template-phases/${phase.id}/`,
                phaseUpdates
              );
            }

            // Gestionar tareas
            const originalTasks = originalPhase.template_tasks || [];
            const originalTaskMap = new Map(
              originalTasks.map((task) => [task.id, task])
            );

            const tasksToDelete = originalTasks.filter(
              (task) => !phase.tasks.some((item) => item.id === task.id)
            );

            for (const task of tasksToDelete) {
              await apiClient.delete(`projects/template-tasks/${task.id}/`);
            }

            for (const [taskIndex, task] of phase.tasks.entries()) {
              if (!task.id) {

                await apiClient.post("projects/template-tasks/", {
                  phase: phase.id,
                  name: task.name,
                  description: task.description,
                  initial_state: task.initial_state || "planeado",
                  documentation_urls: normalizeLinks(task.documentation_urls),
                  days_to_deadline:
                    task.days_to_deadline === ""
                      ? null
                      : Number(task.days_to_deadline),
                  order: task.order || taskIndex + 1,
                });
              } else {
                const originalTask = originalTaskMap.get(task.id);
                if (!originalTask) continue;

                const taskUpdates = {};
                const documentationUrls = normalizeLinks(task.documentation_urls);
                const originalUrls = originalTask.documentation_urls || [];
                if (JSON.stringify(documentationUrls) !== JSON.stringify(originalUrls)) {
                  taskUpdates.documentation_urls = documentationUrls;
                }

                if (task.name !== originalTask.name) {
                  taskUpdates.name = task.name;
                }
                if (
                  (task.description || "") !== (originalTask.description || "")
                ) {
                  taskUpdates.description = task.description;
                }
                if (
                  (task.initial_state || "planeado") !==
                  (originalTask.initial_state || "planeado")
                ) {
                  taskUpdates.initial_state = task.initial_state;
                }

                const dayValue =
                  task.days_to_deadline === ""
                    ? null
                    : Number(task.days_to_deadline);
                if ((originalTask.days_to_deadline ?? null) !== dayValue) {
                  taskUpdates.days_to_deadline = dayValue;
                }
                if ((task.order || taskIndex + 1) !== originalTask.order) {
                  taskUpdates.order = task.order || taskIndex + 1;
                }

                if (Object.keys(taskUpdates).length > 0) {
                  await apiClient.patch(
                    `projects/template-tasks/${task.id}/`,
                    taskUpdates
                  );
                }
              }
            }
          }
        }
      }

      onSaved();
      onClose();
    } catch (error) {
      console.error(
        isProjectDefinitionMode
          ? "Error al guardar el proyecto"
          : "Error al guardar la plantilla",
        error
      );
      setFormError(extractErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={() => {
        if (!isSaving) onClose();
      }}
      disableBackdropClose={disableBackdropClose || isSaving}
      title={
        titleOverride ||
        (mode === "create"
          ? "Nueva plantilla"
          : canEdit
            ? "Editar plantilla"
            : "Detalle de plantilla")
      }
      maxWidth={mode === "edit" ? "lg" : "md"}
      {...dialogProps}
      contentSx={{
        p: 0,
        display: "flex",
        flexDirection: "column",
        height: "70vh",
        maxHeight: "70vh",
        overflow: "hidden",
      }}
    >
      {open && mode !== "create" && !isFormReady ? (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 3 }}>
          <Stack spacing={3}>
            <Skeleton variant="text" width="35%" height={24} />
            <Skeleton variant="rectangular" height={44} />
            <Skeleton variant="text" width="40%" height={24} />
            <Skeleton variant="rectangular" height={96} />
            <Skeleton variant="text" width="45%" height={24} />
            <Skeleton variant="rectangular" height={140} />
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
          }}
        >
          <Box ref={scrollContainerRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 3 }}>
            <Stack spacing={3}>
              {/* Agency is auto-set by backend based on user role:
                  - SuperAdmin: agency = null (global custom template)
                  - Agency Admin: agency = user's agency (agency custom template)
              */}
              {isSuperAdmin && mode !== "create" && (
                <Box>
                  <Label>Agencia</Label>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 1,
                    }}
                  >
                    {agencyLabel}
                  </Typography>
                </Box>
              )}

              <Box data-tutorial="template-editor-basic-fields">
                <Box>
                  <Label>Nombre</Label>
                  <CustomTextField
                    value={formState.name}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                    disabled={!isEditable || isSaving || isSetupTemplate}
                  />
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Label>Descripción</Label>
                  <CustomTextArea
                    value={formState.description}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        description: event.target.value,
                      }))
                    }
                    disabled={!isEditable || isSaving}
                  />
                </Box>
              </Box>

              {/* Selector de modo de creación */}
              {mode === "create" && creationMode === null && isEditable && !isSaving && (
                <Box data-tutorial="template-editor-structure-mode">
                  <Label>Estructura del proyecto</Label>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                    Elige cómo quieres organizar tu proyecto:
                  </Typography>
                  <Stack spacing={2}>
                    <Button
                      variant="outline"
                      onClick={() => handleSelectCreationMode('phases')}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        height: "auto",
                        py: 2,
                        px: 2.5,
                      }}
                    >
                      <Stack spacing={0.5} sx={{ width: "100%" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Layers size={20} />
                          <Typography variant="body1" fontWeight={600}>
                            Fases con tareas
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Organiza el proyecto en fases, cada una con sus propias tareas
                        </Typography>
                      </Stack>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleSelectCreationMode('tasks')}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        height: "auto",
                        py: 2,
                        px: 2.5,
                      }}
                    >
                      <Stack spacing={0.5} sx={{ width: "100%" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CheckCircle2 size={20} />
                          <Typography variant="body1" fontWeight={600}>
                            Solo tareas
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Lista simple de tareas sin agrupar en fases
                        </Typography>
                      </Stack>
                    </Button>
                  </Stack>
                </Box>
              )}

              {/* Fases y tareas */}
              {creationMode !== null && (
                <Stack spacing={2} data-tutorial="template-editor-example-phases">
                  {creationMode === 'tasks' ? (
                    // Modo solo tareas: estructura idéntica a modo fases
                    <>
                      <Box>
                        <Label sx={{ mb: 2 }}>Tareas</Label>
                        {formState.phases[0]?.tasks.length > 0 ? (
                          <Stack spacing={2}>
                            {formState.phases[0].tasks.map((task) => {
                            const urlError = urlErrors[`${formState.phases[0].tempId}_${task.tempId}`];
                            return (
                              <EditableTaskItem
                                key={task.tempId}
                                task={task}
                                phaseTempId={formState.phases[0].tempId}
                                onTaskChange={handleTaskChange}
                                onRemoveTask={handleRemoveTask}
                                onTaskDragStart={handleTaskDragStart}
                                onTaskDragUpdate={handleTaskDragUpdate}
                                onTaskDragLeave={handleTaskDragLeave}
                                onTaskDrop={handleTaskDrop}
                                onDragMove={handleDragMoveAutoScroll}
                                onDragEnd={clearDragState}
                                isDragging={
                                  dndEnabled &&
                                  draggedTaskRef?.phaseTempId === formState.phases[0].tempId &&
                                  draggedTaskRef?.taskTempId === task.tempId
                                }
                                dropIndicatorPosition={
                                  dndEnabled &&
                                  taskDropIndicator?.phaseTempId === formState.phases[0].tempId &&
                                    taskDropIndicator?.taskTempId === task.tempId
                                    ? taskDropIndicator.position
                                    : null
                                }
                                dndEnabled={dndEnabled}
                                disabled={!isEditable || isSaving}
                                urlError={urlError}
                              />
                            );
                          })}
                        </Stack>
                      ) : (
                        <EmptyStateMessage
                          icon={CheckCircle2}
                          message="No hay tareas. Añade la primera tarea para comenzar."
                          sx={{ py: 4 }}
                        />
                      )}
                      </Box>
                      {isEditable && !isSaving && (
                        <Button
                          fullWidth
                          variant="outline"
                          onClick={() => handleAddTask(formState.phases[0]?.tempId)}
                          disabled={!formState.phases[0]}
                        >
                          <Plus size={16} style={{ marginRight: "8px" }} />
                          Añadir tarea
                        </Button>
                      )}
                    </>
                  ) : (
                    // Modo fases: mostrar las fases con sus tareas
                    <>
                      {formState.phases.map((phase, index) => {
                        const isExpanded = expandedPhases.has(phase.tempId);
                        return (
                          <EditablePhaseCard
                      key={phase.tempId}
                      phase={phase}
                      index={index}
                      isExpanded={isExpanded}
                      onToggleExpand={() => togglePhase(phase.tempId)}
                      onPhaseChange={handlePhaseChange}
                      onAddTask={handleAddTask}
                      onRemovePhase={handleRemovePhase}
                      onPhaseDragStart={handlePhaseDragStart}
                      onPhaseDragUpdate={handlePhaseDragUpdate}
                      onPhaseDragLeave={handlePhaseDragLeave}
                      onPhaseDrop={handlePhaseDrop}
                      onPhaseHandleGrab={handlePhaseHandleGrab}
                      onDragMove={handleDragMoveAutoScroll}
                      onDragEnd={clearDragState}
                      isDragging={
                        dndEnabled && draggedPhaseTempId === phase.tempId
                      }
                      dropIndicatorPosition={
                        dndEnabled &&
                          phaseDropIndicator?.phaseTempId === phase.tempId
                          ? phaseDropIndicator.position
                          : null
                      }
                      dndEnabled={dndEnabled}
                      disabled={!isEditable || isSaving}
                    >
                      <Box>
                        <Box
                          component="h5"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            color: theme.palette.text.secondary,
                            mb: 3,
                            margin: "0 0 12px 0",
                          }}
                        >
                          Tareas de la fase:
                        </Box>
                        <Stack spacing={2}>
                          {phase.tasks.length === 0 && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                mt: 1,
                              }}
                            >
                              Aún no hay tareas en esta fase.
                            </Typography>
                          )}
                          {phase.tasks.map((task) => {
                            const errorKey = `${phase.tempId}_${task.tempId}`;
                            const urlError = urlErrors[errorKey];

                            return (
                              <EditableTaskItem
                                key={task.tempId}
                                task={task}
                                phaseTempId={phase.tempId}
                                onTaskChange={handleTaskChange}
                                onRemoveTask={handleRemoveTask}
                                onTaskDragStart={handleTaskDragStart}
                                onTaskDragUpdate={handleTaskDragUpdate}
                                onTaskDragLeave={handleTaskDragLeave}
                                onTaskDrop={handleTaskDrop}
                                onDragMove={handleDragMoveAutoScroll}
                                onDragEnd={clearDragState}
                                isDragging={
                                  dndEnabled &&
                                  draggedTaskRef?.phaseTempId === phase.tempId &&
                                  draggedTaskRef?.taskTempId === task.tempId
                                }
                                dropIndicatorPosition={
                                  dndEnabled &&
                                  taskDropIndicator?.phaseTempId === phase.tempId &&
                                    taskDropIndicator?.taskTempId === task.tempId
                                    ? taskDropIndicator.position
                                    : null
                                }
                                dndEnabled={dndEnabled}
                                disabled={!isEditable || isSaving}
                                urlError={urlError}
                              />
                            );
                          })}
                        </Stack>
                      </Box>
                    </EditablePhaseCard>
                  );
                })}

                      {isEditable && !isSaving && (
                        <Button fullWidth variant="outline" onClick={handleAddPhase}>
                          <Plus size={16} style={{ marginRight: "8px" }} />
                          Añadir fase
                        </Button>
                      )}
                    </>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>

          <Divider />

          <Box
            sx={{
              p: "16px 24px",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              flexShrink: 0,
              backgroundColor: "background.paper",
            }}
          >
            {formError && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ color: "error.main" }}
              >
                <AlertCircle size={18} />
                <Typography variant="body2">{formError}</Typography>
              </Stack>
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <Button variant="outline" onClick={onClose} disabled={isSaving}>
                {isEditable ? "Cancelar" : "Cerrar"}
              </Button>
              {isEditable && (
                <Button
                  data-tutorial="template-editor-submit"
                  onClick={saveTemplate}
                  disabled={isSaving}
                >
                  {submitLabelOverride ||
                    (mode === "create" ? "Crear plantilla" : "Guardar cambios")}
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      )}
    </UnifiedModal>
  );
};

export function ProjectTemplates() {
  const tutorialsEnabled = useTutorialsEnabled();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { user: currentUser, interceptorReady } = useAuth();
  const { setPageContext, selectedFilter, filterRefresh, projectsRefreshKey } =
    useOutletContext();

  const loadMoreRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(() => Date.now());
  const [editorOpen, setEditorOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [templateDetail, setTemplateDetail] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [templatesPage, setTemplatesPage] = useState(1);
  const [templatesList, setTemplatesList] = useState([]);
  const [hasMoreTemplates, setHasMoreTemplates] = useState(true);
  const [viewMode, setViewMode] = useState("table");
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortField, setSortField] = useState("created_at");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  const [sortRefreshKey, setSortRefreshKey] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(
    TEMPLATES_TABLE_DEFAULT_PAGE_SIZE
  );
  const searchDebounceTimerRef = useRef(null);
  const lastSearchRef = useRef("");

  const isSuperAdmin = currentUser?.role === "superadmin";
  const canEdit = isSuperAdmin || currentUser?.role === "agency_admin";

  // ── TUTORIAL (proyectos personalizados) ───────────────────────────────────
  // Init sincrónico desde URL params: tutorialActive = true en el primer render
  // → los skeletons y el fetch del backend nunca se ejecutan.
  const [tutorialPhase, setTutorialPhase] = useState(() => {
    if (searchParams.get(CUSTOM_PROJECTS_TUTORIAL_PARAM) !== CUSTOM_PROJECTS_TUTORIAL_VALUE) {
      return null;
    }

    // Solo fases que arrancan sin el editor abierto.
    const phaseParam = searchParams.get(CUSTOM_PROJECTS_TUTORIAL_PHASE_PARAM);
    return phaseParam === CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_CREATED
      ? phaseParam
      : CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATES_OVERVIEW;
  });
  const [tutorialJoyrideKey, setTutorialJoyrideKey] = useState(0);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [isTutorialJoyrideRunning, setIsTutorialJoyrideRunning] = useState(false);
  const [tutorialEditorOpen, setTutorialEditorOpen] = useState(false);
  const tutorialAutoStartedRef = useRef(false);

  const tutorialActive = tutorialsEnabled && tutorialPhase !== null;
  // El tutorial se hace sobre la vista de tabla, sea cual sea la vista elegida.
  const effectiveViewMode = tutorialActive ? "table" : viewMode;
  const isTableView = effectiveViewMode === "table";
  const templatesOrdering = useMemo(
    () => (sortDirection === "desc" ? `-${sortField}` : sortField),
    [sortDirection, sortField]
  );
  const currentTemplatesPageSize = isTableView
    ? tableRowsPerPage
    : TEMPLATES_PAGE_SIZE;

  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();

      if (trimmed === "" && lastSearchRef.current !== "") {
        lastSearchRef.current = "";
        setSearchTerm("");
        setTemplatesPage(1);
        setTemplatesList([]);
        setHasMoreTemplates(true);
        setSearchRefreshKey((prev) => prev + 1);
        return;
      }

      if (trimmed.length >= 2 && trimmed !== lastSearchRef.current) {
        lastSearchRef.current = trimmed;
        setSearchTerm(trimmed);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Arrancar Joyride una sola vez al montar en modo tutorial.
  // No limpiamos la URL mientras estamos aquí para no desmontar el estado del tutorial.
  // Joyride arranca cuando el target del paso actual está montado y quieto (ver efecto más abajo).
  const [tutorialRunRequest, setTutorialRunRequest] = useState(0);
  const requestTutorialRun = useCallback(() => setTutorialRunRequest((n) => n + 1), []);

  useEffect(() => {
    if (!tutorialPhase || tutorialAutoStartedRef.current) return;
    tutorialAutoStartedRef.current = true;
    setTutorialJoyrideKey((k) => k + 1);
    requestTutorialRun();
  }, [tutorialPhase, requestTutorialRun]);

  const sourceProjectsPath = useMemo(() => {
    const from = searchParams.get("from");
    return from === "recurrent" ? "/projects/recurrent" : "/projects/custom";
  }, [searchParams]);

  const advanceTemplateTutorial = useCallback(
    (nextPhase, { closeModal, openModal, startStepIndex = 0 } = {}) => {
      setIsTutorialJoyrideRunning(false);
      if (closeModal) closeModal();
      setTutorialPhase(nextPhase);
      setTutorialStepIndex(startStepIndex);
      setTutorialJoyrideKey((k) => k + 1);
      if (openModal) openModal();
      requestTutorialRun();
    },
    [requestTutorialRun]
  );

  const templateFirstStepOnBackClick = useMemo(() => {
    switch (tutorialPhase) {
      case CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATES_OVERVIEW:
        return () => navigate(
          `${sourceProjectsPath}?${CUSTOM_PROJECTS_TUTORIAL_PARAM}=${CUSTOM_PROJECTS_TUTORIAL_VALUE}&${CUSTOM_PROJECTS_TUTORIAL_PHASE_PARAM}=${CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECTS_OVERVIEW}&${CUSTOM_PROJECTS_TUTORIAL_STEP_PARAM}=${CUSTOM_PROJECTS_OVERVIEW_STEPS.length - 1}`
        );
      case CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_SETUP:
        return () => advanceTemplateTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATES_OVERVIEW, {
          closeModal: () => setTutorialEditorOpen(false),
          startStepIndex: CUSTOM_PROJECTS_TEMPLATES_OVERVIEW_STEPS.length - 1,
        });
      case CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_EXAMPLE:
        return () => advanceTemplateTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_SETUP, {
          startStepIndex: CUSTOM_PROJECTS_TEMPLATE_MODAL_SETUP_STEPS.length - 1,
        });
      case CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_CREATED:
        return () => advanceTemplateTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_EXAMPLE, {
          openModal: () => setTutorialEditorOpen(true),
          startStepIndex: CUSTOM_PROJECTS_TEMPLATE_MODAL_EXAMPLE_STEPS.length - 1,
        });
      default:
        return undefined;
    }
  }, [tutorialPhase, advanceTemplateTutorial, navigate, sourceProjectsPath]);

  const tutorialJoyrideSteps = useMemo(
    () =>
      withTutorialProxyTargets(
        getCustomProjectsPhaseSteps(tutorialPhase, currentUser?.role),
        "templates"
      ).map((step, i) => ({
        ...step,
        ...(i === 0 && templateFirstStepOnBackClick
          ? { onBackClick: templateFirstStepOnBackClick }
          : {}),
      })),
    [tutorialPhase, templateFirstStepOnBackClick, currentUser?.role]
  );

  const isModalTutorialPhase = [
    CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_SETUP,
    CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_EXAMPLE,
  ].includes(tutorialPhase);
  const tutorialJoyrideZIndex = isModalTutorialPhase ? 200000 : 10000;

  const resetTemplateTutorial = useCallback(() => {
    setIsTutorialJoyrideRunning(false);
    setTutorialEditorOpen(false);
    navigate(sourceProjectsPath);
  }, [navigate, sourceProjectsPath]);

  useEffect(() => {
    if (!tutorialRunRequest || !tutorialActive) return undefined;
    const step = tutorialJoyrideSteps[tutorialStepIndex];
    if (!step) return undefined;
    return runWhenTutorialTargetReady(
      step.tutorialSourceTarget,
      () => setIsTutorialJoyrideRunning(true),
      { onTimeout: resetTemplateTutorial }
    );
    // Solo al pedir un arranque (fase, paso y pasos ya están al día en ese render) o al salir
    // del tutorial, que cancela la espera pendiente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialRunRequest, tutorialActive]);

  const handleTemplateTutorialJoyrideCallback = useCallback(
    ({ action, index, status, type }) => {
      if (status === STATUS.FINISHED) {
        setIsTutorialJoyrideRunning(false);
        switch (tutorialPhase) {
          case CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATES_OVERVIEW:
            advanceTemplateTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_SETUP, {
              openModal: () => setTutorialEditorOpen(true),
            });
            break;
          case CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_SETUP:
            advanceTemplateTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_EXAMPLE);
            break;
          case CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_EXAMPLE:
            setTutorialEditorOpen(false);
            advanceTemplateTutorial(CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_CREATED);
            break;
          case CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_CREATED:
            navigate(
              `${sourceProjectsPath}?${CUSTOM_PROJECTS_TUTORIAL_PARAM}=${CUSTOM_PROJECTS_TUTORIAL_VALUE}&${CUSTOM_PROJECTS_TUTORIAL_PHASE_PARAM}=${CUSTOM_PROJECTS_TUTORIAL_PHASES.PROJECT_CREATE_TRIGGER}`
            );
            break;
          default:
            break;
        }
      }
      if (status === STATUS.SKIPPED) {
        resetTemplateTutorial();
        return;
      }

      // Target desmontado (p. ej. el editor aún abriéndose): se espera a que vuelva a estar.
      if (type === EVENTS.TARGET_NOT_FOUND) {
        setIsTutorialJoyrideRunning(false);
        requestTutorialRun();
        return;
      }

      if (type === EVENTS.STEP_AFTER) {
        const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        prepareTutorialStep(tutorialJoyrideSteps, nextIndex);
        setTutorialStepIndex(nextIndex);
      }
    },
    [
      tutorialPhase,
      navigate,
      sourceProjectsPath,
      resetTemplateTutorial,
      advanceTemplateTutorial,
      tutorialJoyrideSteps,
      requestTutorialRun,
    ]
  );
  // ── FIN TUTORIAL ──────────────────────────────────────────────────────────

  const agencyId = useMemo(() => {
    if (
      currentUser?.role === "agency_admin" ||
      currentUser?.role === "consultant"
    ) {
      return currentUser.agencyId || null;
    }
    if (currentUser?.role === "superadmin") {
      return selectedFilter || null;
    }
    return null;
  }, [currentUser, selectedFilter]);

  const templatesEndpoint = useMemo(() => {
    if (!currentUser) return null;

    const params = new URLSearchParams();
    params.set("page", templatesPage.toString());
    params.set("page_size", currentTemplatesPageSize.toString());
    params.set("ordering", templatesOrdering);
    if (searchTerm) {
      params.set("search", searchTerm);
    }

    if (
      currentUser.role === "agency_admin" ||
      currentUser.role === "consultant"
    ) {
      params.set("template_type", "custom");
      return `projects/templates/?${params.toString()}`;
    }

    if (currentUser.role === "superadmin") {
      return `projects/templates/?${params.toString()}`;
    }

    return null;
  }, [
    currentTemplatesPageSize,
    currentUser,
    searchTerm,
    templatesOrdering,
    templatesPage,
  ]);

  const shouldFetchTemplates =
    interceptorReady && !!templatesEndpoint && !tutorialActive;

  const {
    data: templatesData,
    isLoading,
    isFetching,
    error: templatesError,
  } = getData(templatesEndpoint, shouldFetchTemplates, [
    templatesEndpoint,
    refreshKey,
    filterRefresh,
    projectsRefreshKey,
    searchRefreshKey,
    sortRefreshKey,
  ]);

  // Update templates list and total count
  useEffect(() => {
    if (tutorialActive) return;
    if (!templatesData) return;

    if (Array.isArray(templatesData)) {
      setTemplatesList(templatesData);
      setHasMoreTemplates(false);
      setTotalTemplates(templatesData.length);
      return;
    }

    const results = Array.isArray(templatesData?.results)
      ? templatesData.results
      : [];
    const totalCount =
      typeof templatesData?.count === "number" ? templatesData.count : null;

    setTemplatesList((prev) => {
      if (isTableView || templatesPage === 1) {
        return results;
      }

      const seen = new Set(prev.map((template) => template.id));
      const merged = results.filter((template) => !seen.has(template.id));
      return [...prev, ...merged];
    });
    if (totalCount !== null) {
      const totalPages = Math.ceil(totalCount / currentTemplatesPageSize);
      setHasMoreTemplates(templatesPage < totalPages);
    } else {
      setHasMoreTemplates(Boolean(templatesData?.next));
    }
    setTotalTemplates(totalCount ?? results.length);
  }, [
    currentTemplatesPageSize,
    isTableView,
    templatesData,
    templatesPage,
    tutorialActive,
  ]);

  useEffect(() => {
    setTemplatesPage(1);
    setTemplatesList([]);
    setHasMoreTemplates(true);
  }, [
    currentUser?.id,
    currentUser?.role,
    filterRefresh,
    projectsRefreshKey,
    refreshKey,
    searchTerm,
    tableRowsPerPage,
    templatesOrdering,
  ]);

  const isLoadingInitial =
    templatesList.length === 0 && (isLoading || isFetching);
  const isLoadingMore = isFetching && templatesPage > 1;

  useEffect(() => {
    setTemplatesPage(1);
  }, [effectiveViewMode]);

  useEffect(() => {
    if (effectiveViewMode !== "cards") return;
    if (!loadMoreRef.current || !hasMoreTemplates) return;
    if (templatesList.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetching) {
          setTemplatesPage((prev) => {
            if (!hasMoreTemplates) return prev;
            if (totalTemplates > 0) {
              const totalPages = Math.ceil(
                totalTemplates / TEMPLATES_PAGE_SIZE
              );
              if (prev >= totalPages) return prev;
            }
            return prev + 1;
          });
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [
    effectiveViewMode,
    hasMoreTemplates,
    isFetching,
    templatesList.length,
    totalTemplates,
  ]);

  const templates = useMemo(() => {
    if (tutorialActive) {
      if (tutorialPhase === CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_CREATED) {
        return [CUSTOM_PROJECTS_TUTORIAL_TEMPLATE_CARD];
      }
      return [];
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return templatesList;

    return templatesList.filter((template) => {
      const name = (template.name || "").toLowerCase();
      const description = (template.description || "").toLowerCase();
      return name.includes(normalizedSearch) || description.includes(normalizedSearch);
    });
  }, [searchTerm, templatesList, tutorialActive, tutorialPhase]);

  const handleTablePageChange = useCallback((newPage) => {
    setTemplatesPage(newPage + 1);
  }, []);

  const handleTableRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setTableRowsPerPage(nextRowsPerPage);
    setTemplatesPage(1);
  }, []);

  const handleSortFieldChange = useCallback((nextSortField) => {
    if (nextSortField === sortField) return;
    setTemplatesPage(1);
    setTemplatesList([]);
    setHasMoreTemplates(true);
    setSortField(nextSortField);
    setSortRefreshKey((prev) => prev + 1);
  }, [sortField]);

  const handleSortDirectionChange = useCallback((nextSortDirection) => {
    if (nextSortDirection === sortDirection) return;
    setTemplatesPage(1);
    setTemplatesList([]);
    setHasMoreTemplates(true);
    setSortDirection(nextSortDirection);
    setSortRefreshKey((prev) => prev + 1);
  }, [sortDirection]);

  useEffect(() => {
    const leftContent = (
      <IconButton
        onClick={() => navigate(sourceProjectsPath)}
        size="small"
        sx={{
          color: "primary.main",
          padding: "4px",
          "&:hover": { backgroundColor: "action.hover" },
        }}
        disableRipple={false}
      >
        <ChevronLeft size={20} />
      </IconButton>
    );

    setPageContext({
      title: "Plantillas de proyectos",
      subtitle: canEdit
        ? "Configura las fases y tareas base que usarás para crear proyectos personalizados"
        : "Consulta las plantillas disponibles para la creación de proyectos",
      showTitle: true,
      rightContent: canEdit ? (
        <Button
          data-tutorial="project-templates-new-button"
          onClick={() => {
            setModalMode("create");
            setTemplateDetail(null);
            setEditorOpen(true);
          }}
        >
          <Plus size={16} />
          Nueva plantilla
        </Button>
      ) : null,
      leftContent,
    });
  }, [canEdit, navigate, setPageContext, sourceProjectsPath, totalTemplates]);

  const showPopup = (message, error = false) => {
    setPopupMessage(message);
    setIsError(error);
    setPopupOpen(true);
  };

  const handleEditTemplate = async (template) => {
    // Use can_edit from template response to determine mode
    setModalMode(template.can_edit ? "edit" : "view");
    setTemplateDetail(null);
    setEditorOpen(true);
    try {
      const response = await apiClient.get(
        `projects/templates/${template.id}/`
      );
      setTemplateDetail(response.data);
    } catch (error) {
      console.error("Error al cargar la plantilla", error);
      showPopup("No se pudo cargar la plantilla seleccionada.", true);
      setEditorOpen(false);
    }
  };

  const handleDeleteTemplate = (template) => {
    setTemplateToDelete(template);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    setDeleteModalOpen(false); // Close modal immediately
    try {
      await apiClient.delete(`projects/templates/${templateToDelete.id}/`);
      showPopup("Plantilla eliminada correctamente.");
      setRefreshKey((prev) => prev + 1);
      setTemplateToDelete(null);
    } catch (error) {
      console.error("Error al eliminar plantilla", error);
      const errorMessage =
        error?.response?.status === 500
          ? "No es posible eliminar esta plantilla, está siendo usada por un proyecto."
          : "No se pudo eliminar la plantilla.";
      showPopup(errorMessage, true);
    }
  };

  const handleSaved = () => {
    setRefreshKey((prev) => prev + 1);
    showPopup("Plantilla guardada correctamente.");
  };

  if (!templatesEndpoint && currentUser) {
    return (
      <EmptyStateContainer
        title="No tienes acceso"
        message="No tienes permisos para ver las plantillas."
        badgeText="Sin acceso"
      />
    );
  }

  if (templatesError) {
    return (
      <EmptyStateContainer
        title="No se pudieron cargar las plantillas"
        message="Revisa tu conexión o vuelve a intentarlo más tarde."
        badgeText="Error"
      />
    );
  }

  const templatesToolbarProps = {
    searchValue: searchInput,
    onSearchChange: setSearchInput,
    searchPlaceholder: "Buscar plantillas...",
    toolbarEnd: !tutorialActive ? (
      <ViewModeSelector
        options={VIEW_MODE_OPTIONS}
        value={effectiveViewMode}
        onChange={setViewMode}
        tooltipTitle="Escoger vista"
        buttonSx={toolbarViewModeButtonSx}
      />
    ) : null,
    ...(!tutorialActive && {
      sortFieldOptions: SORT_FIELD_OPTIONS,
      sortField,
      sortDirection,
      sortDirectionOptions: SORT_DIRECTION_OPTIONS,
      onSortFieldChange: handleSortFieldChange,
      onSortDirectionChange: handleSortDirectionChange,
    }),
  };

  return (
    <Box>
      {effectiveViewMode !== "table" ? (
        <DataTableToolbar variant="standalone" sx={{ mb: 3 }} {...templatesToolbarProps} />
      ) : null}

      {effectiveViewMode === "table" && tutorialActive ? (
        <ProjectTemplatesTableView
          rows={templates}
          totalCount={templates.length}
          isFetching={false}
          currentPage={0}
          rowsPerPage={tableRowsPerPage}
          onPageChange={noopTutorialHandler}
          onRowsPerPageChange={noopTutorialHandler}
          getBadgeInfo={getTemplateBadgeInfo}
          emptyMessage="Aún no hay plantillas disponibles"
          toolbarProps={templatesToolbarProps}
          getRowProps={getTutorialTemplateRowProps}
        />
      ) : effectiveViewMode === "table" ? (
        <ProjectTemplatesTableView
          rows={templates}
          totalCount={typeof totalTemplates === "number" ? totalTemplates : templates.length}
          isFetching={isFetching}
          currentPage={Math.max(templatesPage - 1, 0)}
          rowsPerPage={tableRowsPerPage}
          onPageChange={handleTablePageChange}
          onRowsPerPageChange={handleTableRowsPerPageChange}
          onEdit={tutorialActive ? undefined : handleEditTemplate}
          onDelete={tutorialActive ? undefined : handleDeleteTemplate}
          getBadgeInfo={getTemplateBadgeInfo}
          emptyMessage={
            searchTerm.trim()
              ? "No hay plantillas que coincidan con la búsqueda"
              : "Aún no hay plantillas disponibles"
          }
          toolbarProps={templatesToolbarProps}
        />
      ) : isLoadingInitial && !tutorialActive ? (
        <Grid container spacing={3}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={`template-skeleton-${index}`}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Skeleton variant="text" width="40%" height={24} />
                  <Skeleton variant="text" width="65%" height={18} sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : templates.length === 0 && (!isFetching || tutorialActive) ? (
        <Card sx={{ flex: 1 }} data-tutorial="project-templates-list">
          <CardContent
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
              padding: 6,
            }}
          >
            <EmptyStateMessage
              icon={Layers}
              message={
                searchTerm.trim()
                  ? "No hay plantillas que coincidan con la búsqueda"
                  : "Aún no hay plantillas disponibles"
              }
              iconSize={48}
            />
          </CardContent>
        </Card>
      ) : (
        <Box
          data-tutorial="project-templates-list"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            gap: 3,
            alignItems: "stretch",
            width: "100%",
            minWidth: 0,
          }}
        >
          {templates.map((template) => (
            <Box key={template.id} sx={{ height: "100%", minWidth: 0 }}>
              <TemplateCard
                template={template}
                onEdit={tutorialActive ? undefined : handleEditTemplate}
                onDelete={tutorialActive ? undefined : handleDeleteTemplate}
              />
            </Box>
          ))}
          {isLoadingMore &&
            hasMoreTemplates &&
            Array.from({ length: 3 }).map((_, index) => (
              <Card key={`template-skeleton-more-${index}`}>
                <CardContent sx={{ p: 3 }}>
                  <Skeleton variant="text" width="40%" height={24} />
                  <Skeleton variant="text" width="65%" height={18} sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            ))}
        </Box>
      )}
      {effectiveViewMode === "cards" && hasMoreTemplates && !tutorialActive && (
        <Box ref={loadMoreRef} sx={{ height: 1 }} />
      )}

      <TemplateEditorModal
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setTemplateDetail(null);
        }}
        initialData={templateDetail}
        mode={modalMode}
        onSaved={handleSaved}
        canEdit={modalMode !== "view" && (templateDetail?.can_edit ?? canEdit)}
        agencyId={agencyId}
        isSuperAdmin={isSuperAdmin}
      />

      {/* Modal de confirmación de eliminación */}
      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`Eliminar plantilla: ${templateToDelete?.name}`}
        question="¿Estás seguro de que quieres eliminar esta plantilla?"
        description={
          <>
            Esta acción no se puede deshacer. Se eliminará permanentemente la plantilla{" "}
            <strong>"{templateToDelete?.name}"</strong> y toda su información asociada.
          </>
        }
        confirmLabel="Eliminar plantilla"
        tone="danger"
      />

      <SuccessPopup
        isOpen={popupOpen}
        message={popupMessage}
        error={isError}
        onClose={() => setPopupOpen(false)}
      />

      {/* ── TUTORIAL: modal de plantilla falso ────────────────────────────── */}
      <TemplateEditorModal
        open={tutorialEditorOpen}
        onClose={resetTemplateTutorial}
        initialData={null}
        mode="create"
        onSaved={() => {}}
        canEdit
        agencyId={agencyId}
        isSuperAdmin={false}
        disableBackdropClose
        dialogProps={TUTORIAL_DIALOG_PROPS}
        tutorialExampleData={
          tutorialPhase === CUSTOM_PROJECTS_TUTORIAL_PHASES.TEMPLATE_MODAL_EXAMPLE
            ? CUSTOM_PROJECTS_TUTORIAL_TEMPLATE_FORM
            : null
        }
      />

      {/* ── TUTORIAL: Joyride multi-fase ──────────────────────────────────── */}
      <TutorialTargetProxies steps={tutorialJoyrideSteps} active={tutorialActive} />
      {/* El scroll hasta cada target lo hace runWhenTutorialTargetReady/prepareTutorialStep. */}
      <Joyride
        floaterProps={TUTORIAL_FLOATER_PROPS}
        key={`templates-tutorial-${tutorialJoyrideKey}`}
        steps={tutorialJoyrideSteps}
        run={tutorialsEnabled && isTutorialJoyrideRunning}
        stepIndex={tutorialStepIndex}
        continuous
        showSkipButton={false}
        showProgress={false}
        disableOverlayClose
        disableCloseOnEsc
        disableScrolling
        disableScrollParentFix
        locale={{ close: "Cerrar", last: "Siguiente", next: "Siguiente" }}
        tooltipComponent={TutorialJoyrideTooltip}
        styles={getTutorialJoyrideStyles(theme, tutorialJoyrideZIndex)}
        callback={handleTemplateTutorialJoyrideCallback}
      />
      {/* ── FIN TUTORIAL ──────────────────────────────────────────────────── */}
    </Box>
  );
}
